import { PageHero, CTABand } from "@/components/site/PageHero";
import { Container, Overline, Reveal } from "@/lib/ui";
import { IMAGES, METRICS, WHY } from "@/data/site";

const values = [
  { title: "Alignment", desc: "We operate as an extension of our clients' investment teams, measured against their return objectives." },
  { title: "Transparency", desc: "Audit-ready reporting and clear communication at every level of the portfolio." },
  { title: "Discipline", desc: "Underwriting-grade rigor applied to daily operating and capital decisions." },
  { title: "Stewardship", desc: "We treat every asset as a long-term holding, not a short-term assignment." },
];

export default function About() {
  return (
    <>
      <PageHero
        id="about"
        overline="About GreatLeaf"
        title="A vertically integrated real estate operating platform."
        lead="GreatLeaf Management provides asset management, property management, operations, leasing, hospitality and investment support for institutional owners across the United States."
        image={IMAGES.skyline}
      />

      <section className="py-24 md:py-32 bg-[#F7F7F5]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-7">
              <Overline>Our Firm</Overline>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl leading-tight text-[#121A16]">
                We exist to maximize the performance of the real estate we manage.
              </h2>
              <div className="mt-8 space-y-5 text-[#5C6660] leading-relaxed max-w-2xl">
                <p>
                  GreatLeaf was built on a simple premise: institutional owners deserve an operator that thinks like an
                  investor. We unite property-level execution with asset-level strategy, so operations, capital and reporting
                  work toward one goal — durable, long-term value.
                </p>
                <p>
                  Across multifamily, build-to-rent, single-family rental, student housing, hospitality, mixed-use and
                  commercial assets, our teams apply a consistent operating standard supported by data, technology and
                  disciplined controls.
                </p>
                <p>
                  We serve family offices, funds, REITs, developers and private owners who measure success not in a single
                  quarter, but across the full lifecycle of an asset.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src={IMAGES.team} alt="GreatLeaf operating team reviewing portfolio performance" loading="lazy" className="w-full h-full object-cover rounded-sm aspect-[4/5]" />
            </div>
          </div>
        </Container>
      </section>

      <section className="py-20 md:py-28 bg-[#1B3B2B] text-white grain-dark relative overflow-hidden">
        <Container className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {METRICS.map((m) => (
              <div key={m.label} className="bg-[#1B3B2B] px-6 py-8">
                <div className="font-data text-4xl">{m.value}</div>
                <div className="mt-2 text-sm text-white/55">{m.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <Container>
          <Overline>What We Stand For</Overline>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="border border-[#E5E5DF] rounded-sm p-7 h-full">
                  <h3 className="font-display text-xl text-[#121A16] mb-2.5">{v.title}</h3>
                  <p className="text-sm text-[#5C6660] leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
